import { useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { Checkbox as CheckboxSchema } from '@/schemas/sceneDataSchema';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import { Checkbox, FormControl, FormControlLabel, FormHelperText } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import useAnalytics from '@/hooks/use-analytics';
import { useFieldBinding, seedFormFromStore } from '@/hooks/use-field-binding';

export default function CheckboxFormElement({ form, component }: { form: UseFormReturn; component: CheckboxSchema }) {
  const { trackEvent } = useAnalytics();
  const fieldName = encodeFieldKey(component);
  const binding = useFieldBinding(component);

  useEffect(() => {
    seedFormFromStore(form, fieldName, binding.value, false);
    // If no server value, stage false in the store and persist it.
    if (binding.value == null) {
      binding.sync(false);
      binding.onBlur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Controller
      name={fieldName}
      control={form.control}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <FormControl
          error={!!fieldState.error}
          sx={{ alignItems: 'flex-start' }}
        >
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                onChange={(_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
                  trackEvent(`${component.attribute}_CHECKED`);
                  onChange(checked);
                  binding.sync(checked);
                  binding.onBlur();
                }}
                value={!!value}
                checked={!!value}
                color={fieldState.error ? 'error' : undefined}
              />
            }
            label={component.label}
          />
          {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
}
